
    // Composed the way core's AnnotationBurn hook (phoenix_kit.js) composes
    // the grid preview, so the document copy looks exactly like it: the same
    // live overlay SVG, with every element's computed look copied onto its
    // clone (a serialised SVG cannot see the page's stylesheet — the
    // dimension numbers' colour and font live there), editor chrome dropped,
    // and the canvas grown to the union of the photo and the ink drawn past
    // its edges. Where core stops at a JPEG capped at 800px, this keeps the
    // photo's own resolution and writes a lossless PNG. Keep the three lists
    // in step with core's BURN_STYLE_PROPS / BURN_CHROME / BURN_STATE.
    const STYLE_PROPS = [
      "fill", "fill-opacity", "fill-rule", "stroke", "stroke-width", "stroke-opacity",
      "stroke-dasharray", "stroke-linecap", "stroke-linejoin", "stroke-miterlimit",
      "opacity", "color", "font-family", "font-size", "font-weight", "font-style",
      "letter-spacing", "text-anchor", "dominant-baseline", "paint-order", "visibility",
      "mix-blend-mode", "filter", "transform", "transform-origin", "display"
    ]
    const CHROME = [
      ".etcher-handle", ".etcher-grab", ".etcher-handle-line", ".etcher-handle-midpoint",
      ".etcher-handle-edge", ".etcher-connector-hit", ".etcher-connector-dot",
      ".etcher-title-handle", ".etcher-snap-guide", ".etcher-marquee", ".is-draft",
      ".etcher-badge"
    ]
    const STATE = ["is-hovered", "is-selected", "is-editing", "is-dragging", "is-close-target"]

    export default {
      dbg(step, data) { console.debug("[flatten]", step, data || "") },
      mounted() { this.dbg("mounted"); this.scan() },
      updated() { this.scan() },
      scan() {
        const stage = this.el.querySelector("[data-flatten-stage]")
        if (!stage) { this.dbg("scan_no_stage"); return }
        if (stage.dataset.started) return
        stage.dataset.started = "1"
        this.dbg("stage_found")
        this.waitReady(stage)
          .then(() => this.capture(stage))
          .catch((e) => this.fail(e && e.message || e))
      },
      fail(reason) {
        this.dbg("fail", {reason: String(reason)})
        this.pushEventTo(this.el.dataset.target, "flatten_failed", {reason: String(reason)})
      },
      overlaySvg(stage) {
        // The annotation layer is the <svg> inside .etcher-overlay (sized
        // 100% of the image area). Etcher also renders small toolbar/icon
        // <svg>s, so a bare querySelector("svg") grabs the wrong one (~18px).
        return stage.querySelector(".etcher-overlay svg")
      },
      host(stage) { return stage.querySelector("[phx-hook='FrescoCanvas']") },
      viewer(stage) {
        const host = this.host(stage)
        return host && window.Fresco && window.Fresco.viewerFor && window.Fresco.viewerFor(host.id)
      },
      waitReady(stage) {
        return new Promise((resolve, reject) => {
          const t0 = Date.now()
          let lastLog = 0
          const tick = () => {
            const img = stage.querySelector("img[data-fresco-canvas-img]")
            const svg = this.overlaySvg(stage)
            const imgReady = img && img.complete && img.naturalWidth > 0
            const svgRect = svg ? svg.getBoundingClientRect() : null
            const svgSized = svgRect && svgRect.width >= 100
            if (imgReady && svgSized && this.viewer(stage)) {
              this.dbg("ready", {w: img.naturalWidth, h: img.naturalHeight,
                svgW: Math.round(svgRect.width), svgH: Math.round(svgRect.height)})
              return resolve()
            }
            const now = Date.now()
            if (now - lastLog > 2000) {
              lastLog = now
              this.dbg("waiting", {
                imgReady: !!imgReady, overlaySvg: !!svg,
                svgW: svgRect ? Math.round(svgRect.width) : -1
              })
            }
            if (now - t0 > 15000) {
              return reject(new Error("not_ready imgReady=" + !!imgReady +
                " overlaySvg=" + !!svg + " svgW=" + (svgRect ? Math.round(svgRect.width) : -1) +
                " viewer=" + !!this.viewer(stage)))
            }
            requestAnimationFrame(tick)
          }
          tick()
        })
      },
      async capture(stage) {
        const plan = this.plan(stage)
        if (!plan) return this.fail("no_plan")
        this.dbg("capture", {out: plan.out, svgLen: plan.svgText.length})

        const overlay = new Image()
        await new Promise((res, rej) => {
          overlay.onload = res
          overlay.onerror = () => rej(new Error("svg_load_failed"))
          overlay.src = "data:image/svg+xml;charset=utf-8," + encodeURIComponent(plan.svgText)
        })

        const canvas = document.createElement("canvas")
        canvas.width = plan.out.w
        canvas.height = plan.out.h
        const ctx = canvas.getContext("2d")
        ctx.imageSmoothingQuality = "high"
        // No background fill: PNG keeps alpha, so ink drawn past the photo's
        // edge sits on transparency and a document shows its own page there.
        const p = plan.picture
        ctx.drawImage(p.img, p.x, p.y, p.w, p.h)
        ctx.drawImage(overlay, 0, 0, plan.out.w, plan.out.h)

        let dataUrl
        try {
          dataUrl = canvas.toDataURL("image/png")
        } catch (e) {
          return this.fail("toDataURL " + (e && e.message || e))
        }
        // An over-limit canvas (e.g. iOS Safari past 16.7 MP) exports "data:,".
        if (!dataUrl.startsWith("data:image/png;base64,")) return this.fail("canvas_too_large")
        this.dbg("png_ready", {bytes: dataUrl.length})
        this.pushEventTo(this.el.dataset.target, "flatten_png", {data: dataUrl})
      },
      // Core's burnCapturePlan with the source fixed to the photo on the stage
      // (the signed same-origin original, so the canvas stays untainted).
      plan(stage) {
        const host = this.host(stage)
        const handle = this.viewer(stage)
        const img = stage.querySelector("img[data-fresco-canvas-img]")
        const svg = this.overlaySvg(stage)
        if (!host || !handle || !img || !svg) return null

        const W = parseFloat(img.dataset.canvasWidth) || img.naturalWidth
        const H = parseFloat(img.dataset.canvasHeight) || img.naturalHeight
        if (!(W > 0 && H > 0)) return null
        // Canvas units → the photo's own pixels.
        const k = (img.naturalWidth || W) / W

        // The overlay is laid out in container px; recover the affine that put
        // it there by asking the viewer where two known image points land.
        const rect = host.getBoundingClientRect()
        const p0 = handle.imageToScreen({x: 0, y: 0})
        const p1 = handle.imageToScreen({x: 1000, y: 0})
        const s = (p1.x - p0.x) / 1000
        if (!(s > 0)) return null
        const tx = p0.x - rect.left, ty = p0.y - rect.top

        // Ink drawn past the photo's edges is part of the markup too: the
        // output is the union of the photo and every shape.
        let minX = 0, minY = 0, maxX = W, maxY = H
        svg.querySelectorAll(".etcher-shape").forEach((el) => {
          const r = el.getBoundingClientRect()
          if (!r.width && !r.height) return
          minX = Math.min(minX, (r.left - rect.left - tx) / s)
          minY = Math.min(minY, (r.top - rect.top - ty) / s)
          maxX = Math.max(maxX, (r.right - rect.left - tx) / s)
          maxY = Math.max(maxY, (r.bottom - rect.top - ty) / s)
        })
        minX = Math.floor(minX); minY = Math.floor(minY)
        maxX = Math.ceil(maxX); maxY = Math.ceil(maxY)

        // Momentary looks (hover ring, selection) must not set in the picture:
        // suspended while styles are read, restored in the same frame.
        const suspended = []
        svg.querySelectorAll("." + STATE.join(", .")).forEach((el) => {
          const had = STATE.filter((c) => el.classList.contains(c))
          if (had.length) {
            suspended.push([el, had])
            had.forEach((c) => el.classList.remove(c))
          }
        })

        const clone = svg.cloneNode(true)
        const live = [svg, ...svg.querySelectorAll("*")]
        const copy = [clone, ...clone.querySelectorAll("*")]
        for (let i = 0; i < live.length; i++) {
          const cs = getComputedStyle(live[i])
          let css = ""
          for (const prop of STYLE_PROPS) {
            const v = cs.getPropertyValue(prop)
            if (v && v !== "normal" && v !== "auto") css += prop + ":" + v + ";"
          }
          copy[i].setAttribute("style", css)
          copy[i].removeAttribute("class")
        }
        suspended.forEach(([el, had]) => had.forEach((c) => el.classList.add(c)))

        CHROME.forEach((sel) => {
          live.forEach((el, idx) => {
            if (el.matches && el.matches(sel) && copy[idx] && copy[idx].remove) copy[idx].remove()
          })
        })

        const NS = "http://www.w3.org/2000/svg"
        const out = {w: Math.round((maxX - minX) * k), h: Math.round((maxY - minY) * k)}
        const g = document.createElementNS(NS, "g")
        const m = k / s
        g.setAttribute("transform",
          "matrix(" + m + ",0,0," + m + "," + (-tx * m - minX * k) + "," + (-ty * m - minY * k) + ")")
        while (clone.firstChild) g.appendChild(clone.firstChild)
        clone.appendChild(g)
        clone.setAttribute("xmlns", NS)
        clone.setAttribute("width", out.w)
        clone.setAttribute("height", out.h)
        clone.setAttribute("viewBox", "0 0 " + out.w + " " + out.h)
        clone.removeAttribute("style")

        return {
          svgText: new XMLSerializer().serializeToString(clone),
          out,
          picture: {img, w: W * k, h: H * k, x: -minX * k, y: -minY * k}
        }
      }
    }
  